import { useEffect, useState } from 'react';
import { api } from '../api';
import { formatEuros } from '../format';
import type { ExpenseCategory, JournalLine, JournalResponse } from '../types';

const MONTHS = [
  'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
];

function monthLabel(month: string): string {
  const index = Number(month.slice(5, 7)) - 1;
  return MONTHS[index] ?? month;
}

/** Zéro n'apporte rien dans une grille : une case vide se lit plus vite. */
function cell(value: number | undefined): string {
  return value ? formatEuros(value) : '';
}

type Row = Readonly<{
  key: string;
  label: string;
  kind: 'room' | 'subtotal' | 'category' | 'net';
  valueFor: (line: JournalLine) => number | undefined;
  totalCents: number;
}>;

export function JournalPanel({ year }: { year: string }) {
  const [data, setData] = useState<JournalResponse | null>(null);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        const [journal, reference] = await Promise.all([
          api<JournalResponse>(`/api/admin/finances/journal?year=${year}`),
          api<{ categories: ExpenseCategory[] }>('/api/admin/finances/categories'),
        ]);
        setData(journal);
        setCategories(reference.categories);
        setError('');
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
      }
    };
    void load();
  }, [year]);

  if (error) return <div className="inline-message error" role="alert">{error}</div>;
  if (!data) return <p className="muted">Chargement…</p>;

  // Seuls les postes réellement mouvementés sont listés : afficher les
  // dix-sept en permanence rendrait la grille illisible. Les mois en colonne,
  // eux, restent bornés à douze quel que soit le nombre de postes utilisés —
  // une grille qui s'allonge plutôt qu'une grille qui s'élargit sans limite.
  const usedCategories = categories.filter((category) =>
    data.lines.some((line) => line.expensesByCategory[category.code]));

  const rows: Row[] = [
    ...data.roomNames.map((room): Row => ({
      key: `room:${room}`,
      label: room,
      kind: 'room',
      valueFor: (line) => line.rentByRoom[room],
      totalCents: data.lines.reduce((sum, line) => sum + (line.rentByRoom[room] ?? 0), 0),
    })),
    {
      key: 'rent-total',
      label: 'Loyers',
      kind: 'subtotal',
      valueFor: (line) => line.rentTotalCents,
      totalCents: data.totals.rentCents,
    },
    ...usedCategories.map((category): Row => ({
      key: `category:${category.code}`,
      label: category.label,
      kind: 'category',
      valueFor: (line) => line.expensesByCategory[category.code],
      totalCents: data.lines.reduce((sum, line) => sum + (line.expensesByCategory[category.code] ?? 0), 0),
    })),
    {
      key: 'expenses-total',
      label: 'Dépenses',
      kind: 'subtotal',
      valueFor: (line) => line.expensesTotalCents,
      totalCents: data.totals.expensesCents,
    },
    {
      key: 'net',
      label: 'Net',
      kind: 'net',
      valueFor: (line) => line.netCents,
      totalCents: data.totals.netCents,
    },
  ];

  return (
    <section className="journal">
      {/* Les quatre totaux d'abord : c'est ce qu'on vient chercher. Le tableau,
          large et dense, se déplie derrière. */}
      <dl className="kpi-grid">
        <div className="kpi">
          <dt>Recettes encaissées</dt>
          <dd>{formatEuros(data.totals.rentCents)}</dd>
        </div>
        <div className="kpi">
          <dt>Charges déductibles</dt>
          <dd>{formatEuros(data.totals.deductibleCents)}</dd>
        </div>
        <div className="kpi">
          <dt>Résultat avant amortissements</dt>
          <dd>{formatEuros(data.totals.taxableBeforeDepreciationCents)}</dd>
        </div>
        <div className={data.totals.netCents < 0 ? 'kpi alert' : 'kpi'}>
          <dt>Net de trésorerie</dt>
          <dd>{formatEuros(data.totals.netCents)}</dd>
        </div>
      </dl>

      <div className="deposit-note">
        <strong>Ce chiffre n'est pas votre résultat imposable</strong>
        <span>
          Les amortissements par composants (structure, façade, équipement, agencement, mobilier) viennent
          encore en déduction et l'annulent souvent. Ils relèvent de votre liasse 2033 et ne sont pas calculés
          ici. Le remboursement du capital, lui, sort de la trésorerie sans jamais réduire l'impôt.
        </span>
      </div>

      <details className="access-panel journal-details" open>
        <summary>Livre comptable {data.year} — {data.lines.length} mois mouvementé(s)</summary>
        <p className="section-intro">
          Les loyers sont pris à la date d'encaissement, pas à celle de l'échéance : en LMNP, le résultat se
          calcule sur les recettes réellement perçues.
        </p>

      {data.lines.length === 0
        ? <div className="empty-state">Aucun mouvement enregistré pour {data.year}.</div>
        : (
          <div className="journal-scroll">
            <table className="journal-table">
              <thead>
                <tr>
                  <th scope="col">Poste</th>
                  {data.lines.map((line) => <th scope="col" key={line.month}>{monthLabel(line.month)}</th>)}
                  <th scope="col" className="subtotal">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <th scope="row" title={row.kind === 'category' ? categories.find((c) => `category:${c.code}` === row.key)?.note : undefined}>
                      {row.label}
                    </th>
                    {data.lines.map((line) => (
                      <td key={line.month} className={row.kind === 'subtotal' || row.kind === 'net' ? 'subtotal' : undefined}>
                        {cell(row.valueFor(line))}
                      </td>
                    ))}
                    <td className={row.kind === 'net' && row.totalCents < 0 ? 'subtotal warn-text' : 'subtotal'}>
                      {formatEuros(row.totalCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </details>
    </section>
  );
}
